/**
 * @author EdenCha <eden@nurigo.net>
 */
import React from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import withWidth, { isWidthDown, isWidthUp } from '@material-ui/core/withWidth'
import classNames from 'classnames'
import cookies from 'js-cookie'

import Grid from '@material-ui/core/Grid'
import AppBar from '@material-ui/core/AppBar'
import Drawer from '@material-ui/core/Drawer'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Hidden from '@material-ui/core/Hidden'
import Icon from '@material-ui/core/Icon'
import Collapse from '@material-ui/core/Collapse'
import LinearProgress from '@material-ui/core/LinearProgress'
import Box from '@material-ui/core/Box'
import Alert from '@material-ui/lab/Alert'

// my components
import DrawerMenu from 'components/molecules/DrawerMenu'
import MiniButton from 'components/atoms/MiniButton'
import LayoutHeaderMenuContainer from 'containers/LayoutHeaderMenuContainer'

// static
import LogoImg from 'static/image/logo.png'

import config from 'config'
import { checkUpdate } from 'lib/version'

const drawerWidth = 240
const styles = theme => ({
  root: {
    flexGrow: 1,
    zIndex: 1,
    position: 'relative',
    display: 'flex'
  },
  mobileRoot: {
    overflow: 'hidden'
  },
  appBar: {
    backgroundColor: '#fff',
    marginLeft: drawerWidth,
    boxShadow: '0px 4px 8px -3px rgba(17, 17, 17, .06)',
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`
    }
  },
  layoutVersionText: {
    position: 'absolute',
    right: 3,
    top: 0,
    fontSize: '0.5em',
    color: 'rgba(0,0,0,0.2)'
  },
  toolbar: theme.mixins.toolbar,
  nonDrawerAppBar: {
    backgroundColor: '#fff',
    boxShadow: '0px 4px 8px -3px rgba(17, 17, 17, .06)',
    width: '100%'
  },
  navIconHide: {
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  drawerHidden: {
    width: '100%',
    maxWidth: drawerWidth
  },
  drawerPaper: {
    width: drawerWidth,
    maxWidth: '80%',
    '&::-webkit-scrollbar': {
      width: 5
      // display: 'none'
    },
    [theme.breakpoints.up('md')]: {
      width: drawerWidth
    }
  },
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1.5),
    paddingBottom: 70,
    minHeight: '100vh',
    boxSizing: 'border-box',
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
      paddingBottom: 70
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  nonDrawerContent: {
    margin: '0 auto',
    padding: theme.spacing(1),
    paddingBottom: 70,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      margin: '0 auto'
    }
  },
  logoAnchor: {
    display: 'block',
    height: '1.5em'
  },
  logoImg: {
    display: 'block',
    height: '100%'
  },
  headerTitle: {
    flex: 1,
    position: 'relative',
    paddingLeft: 10,
    color: '#666',
    fontSize: '1.2em',
    fontWeight: 300,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  progressArea: {
    position: 'absolute',
    top: 0,
    width: '100%'
  }
})

const ConsoleLayout = ({ classes, ...others }) => {
  const {
    width,
    children,
    theme,
    menuOpen,
    favoriteMenu = [],
    routeParams = {},
    layoutLoading,
    menus = [],
    auth,
    onClickDrawerToggle,
    onCloseDrawer,
    drawerRef,
    onClickFavoriteIcon,
    onClickSwitchTrial
  } = others
  const { drawer = true, title } = routeParams
  const versionData = checkUpdate()
  const currentVersion = versionData?.currentVersion?.version
  const advancedMode = cookies.get('advancedMode')
  return (
    <div
      className={classNames(classes.root, {
        [classes.mobileRoot]: isWidthDown('xs', width)
      })}
    >
      <AppBar className={drawer ? classes.appBar : classes.nonDrawerAppBar}>
        <div className={classes.layoutVersionText}>v{currentVersion}</div>
        <Collapse in={layoutLoading} className={classes.progressArea}>
          <div>
            <LinearProgress />
          </div>
        </Collapse>
        <Toolbar>
          <Grid
            container
            justifyContent="space-between"
            wrap="nowrap"
            alignItems="center"
          >
            <Grid item xs>
              <Grid container wrap="nowrap" alignItems="center">
                <Grid item>
                  <IconButton
                    aria-label="open drawer"
                    onClick={onClickDrawerToggle}
                    className={classes.navIconHide}
                  >
                    <Icon>menu</Icon>
                  </IconButton>
                </Grid>
                <Grid item>
                  <Hidden smDown>
                    <a href={config.homePath} className={classes.logoAnchor}>
                      <img
                        className={classes.logoImg}
                        src={LogoImg}
                        alt="logo"
                        title="logo"
                      />
                    </a>
                  </Hidden>
                </Grid>
                <Grid item>
                  <Typography
                    variant="title"
                    color="default"
                    className={classes.headerTitle}
                    noWrap
                  >
                    {title}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs="auto">
              <Box>
                <LayoutHeaderMenuContainer />
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {drawer && isWidthDown('sm', width) && (
        <Hidden mdUp className={classes.drawerHidden}>
          <Drawer
            ref={drawerRef}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={menuOpen}
            onClose={onCloseDrawer}
            classes={{
              paper: classNames(classes.drawerPaper, 'drawerMenuPaper')
            }}
            ModalProps={{ keepMounted: true }}
          >
            <DrawerMenu
              menus={menus}
              auth={auth}
              favoriteMenu={favoriteMenu}
              onClickFavoriteIcon={onClickFavoriteIcon}
            />
          </Drawer>
        </Hidden>
      )}
      {drawer && isWidthUp('md', width) && (
        <Hidden smDown implementation="css" className={classes.drawerHidden}>
          <Drawer
            open
            ref={drawerRef}
            variant="permanent"
            classes={{
              paper: classNames(classes.drawerPaper, 'drawerMenuPaper')
            }}
          >
            <DrawerMenu
              menus={menus}
              auth={auth}
              favoriteMenu={favoriteMenu}
              onClickFavoriteIcon={onClickFavoriteIcon}
            />
          </Drawer>
        </Hidden>
      )}
      <main className={drawer ? classes.content : classes.nonDrawerContent}>
        <div className={classes.toolbar} />
        {config?.isAdmin && (
          <Box mb={2}>
            <Alert elevation={1} variant="filled" severity="info">
              ADMIN FRONTEND 조회중 ({auth?.accountId})
            </Alert>
          </Box>
        )}
        {auth?.targetAccountId && (
          <Box mb={2}>
            <Alert elevation={1} variant="filled" severity="error">
              <Box>
                <Grid container>
                  <Grid item xs>
                    <Box mb={1}>경고! 사용자 계정 미리보기 진행중</Box>
                    <Box>
                      {!advancedMode
                        ? '고급 모드 꺼짐 (개인 설정 저장 불가)'
                        : '고급 모드 켜짐'}

                      <MiniButton
                        style={{ marginLeft: 4 }}
                        onClick={() => {
                          advancedMode
                            ? cookies.remove('advancedMode', {
                                domain: config.cookieDomain
                              })
                            : cookies.set('advancedMode', '1', {
                                domain: config.cookieDomain
                              })
                          window.location.reload()
                        }}
                      >
                        {advancedMode ? '고급 모드 끄기' : '고급 모드 켜기'}
                      </MiniButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Alert>
          </Box>
        )}
        {auth?.isTrial && (
          <Box
            mb={2}
            style={{
              cursor: 'pointer'
            }}
            onClick={() => {
              onClickSwitchTrial()
            }}
          >
            <Alert elevation={1} variant="filled" severity="info">
              현재 체험하기를 통해 콘솔 둘러보기를 진행중입니다.
            </Alert>
          </Box>
        )}
        {children}
      </main>
    </div>
  )
}
export default withStyles(styles, { withTheme: true })(
  withWidth()(ConsoleLayout)
)
